import { useEffect, useRef } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { CreditCard, Shield, TrendingUp, Smartphone, Globe, Zap, CheckCircle2, ArrowRight, Monitor, Lock, RefreshCw } from "lucide-react";
import { useTranslation } from "@/i18n/translations";

function A({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: "-60px" });
  return <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={v ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>{children}</motion.div>;
}

export default function OnlinePaymentsPage() {
  const { t: tr } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const ADVANTAGES = [
    { icon: TrendingUp, title: tr("opay.conv"), desc: tr("opay.convD") },
    { icon: Zap, title: tr("opay.integ"), desc: tr("opay.integD") },
    { icon: Shield, title: tr("opay.auth"), desc: tr("opay.authD") },
  ];

  const SERVICES = [
    { icon: Globe, title: tr("opay.web"), desc: tr("opay.webD") },
    { icon: Smartphone, title: tr("opay.app"), desc: tr("opay.appD") },
    { icon: RefreshCw, title: tr("opay.recurring"), desc: tr("opay.recurringD") },
  ];

  const INTEG_OPTS = [
    { title: tr("opay.dropin"), desc: tr("opay.dropinD") },
    { title: tr("opay.comp"), desc: tr("opay.compD") },
    { title: tr("opay.api"), desc: tr("opay.apiD") },
    { title: tr("opay.ecom"), desc: tr("opay.ecomD") },
  ];

  const SECURITY = [
    { icon: Lock, title: tr("opay.tds"), desc: tr("opay.tdsD") },
    { icon: Shield, title: tr("opay.pci"), desc: tr("opay.pciD") },
    { icon: CreditCard, title: tr("opay.token"), desc: tr("opay.tokenD") },
    { icon: Monitor, title: tr("opay.ml"), desc: tr("opay.mlD") },
  ];

  return (
    <div data-testid="online-payments-page">
      <SEO page="online" />
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#0A1128]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <A>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("opay.tag")}</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-4 leading-[1.1]">{tr("opay.title")}</h1>
              <p className="text-base md:text-lg text-slate-300 mt-6 leading-relaxed">{tr("opay.desc")}</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-7 py-3 text-sm hover:bg-[#00A844] transition-all">{tr("hero.cta1")} <ArrowRight size={16} /></Link>
                <Link to="/tarifs" className="inline-flex items-center justify-center font-semibold border-2 border-white/20 text-white rounded-full px-7 py-3 text-sm hover:bg-white/10 transition-all">{tr("cta.pricing")}</Link>
              </div>
            </A>
            <A delay={0.2}>
              <img src="https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL0dSQ3NTaHNvSHhqTlZiSEdUOFV6LmpwZyJ9:adyen:k9TvVLbT7piuwHTKvL9O6Gi-VuLwWfpIxypUaKg9E6I?format=webp&width=800&height=320" alt="Checkout" loading="lazy" className="w-full rounded-2xl border border-white/10" />
            </A>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <A className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("opay.advTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{tr("opay.advTitle")}</h2>
          </A>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ADVANTAGES.map((f, i) => (
              <A key={i} delay={i * 0.1}>
                <div className="feature-card bg-[#F8F9FA] rounded-xl border border-slate-100 p-8 h-full">
                  <div className="w-11 h-11 rounded-lg bg-[#00C853]/10 flex items-center justify-center mb-5"><f.icon size={22} className="text-[#00C853]" /></div>
                  <h3 className="text-lg font-semibold text-[#0A1128] mb-2">{f.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{f.desc}</p>
                </div>
              </A>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <A className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("opay.svcTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{tr("opay.svcTitle")}</h2>
          </A>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <A key={i} delay={i * 0.1}>
                <div className="bg-white rounded-xl border border-slate-200 p-8 h-full">
                  <div className="w-11 h-11 rounded-lg bg-[#1B2B5B]/5 flex items-center justify-center mb-5"><s.icon size={22} className="text-[#1B2B5B]" /></div>
                  <h3 className="text-lg font-semibold text-[#0A1128] mb-2">{s.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{s.desc}</p>
                </div>
              </A>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <A>
              <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{tr("opay.methTitle")}</h2>
              <p className="text-base text-[#475569] mt-4 leading-relaxed">{tr("opay.methDesc")}</p>
              <div className="grid grid-cols-2 gap-3 mt-6">
                {["Visa & Mastercard", "American Express", "Twint", "Apple Pay & Google Pay", "PostFinance", "SEPA Direct Debit", "Klarna (BNPL)", "PayPal"].map((m, i) => (
                  <div key={i} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#00C853] shrink-0" /><span className="text-sm text-[#475569]">{m}</span></div>
                ))}
              </div>
            </A>
            <A delay={0.2}>
              <div className="bg-[#0A1128] rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-4">{tr("opay.integOpt")}</h3>
                {INTEG_OPTS.map((o, i) => (
                  <div key={i} className="flex items-start gap-3 mt-4">
                    <div className="w-6 h-6 rounded bg-[#00C853]/10 flex items-center justify-center shrink-0 mt-0.5"><span className="text-xs font-bold text-[#00C853]">{i + 1}</span></div>
                    <div><div className="text-sm font-medium text-white">{o.title}</div><div className="text-xs text-slate-400">{o.desc}</div></div>
                  </div>
                ))}
              </div>
            </A>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <A className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight">{tr("opay.secTitle")}</h2>
          </A>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SECURITY.map((s, i) => (
              <A key={i} delay={i * 0.08}>
                <div className="bg-white rounded-xl border border-slate-200 p-6 text-center h-full">
                  <div className="w-10 h-10 rounded-lg bg-[#00C853]/10 flex items-center justify-center mx-auto mb-4"><s.icon size={18} className="text-[#00C853]" /></div>
                  <h3 className="text-sm font-semibold text-[#0A1128] mb-1">{s.title}</h3>
                  <p className="text-xs text-[#475569]">{s.desc}</p>
                </div>
              </A>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#1B2B5B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <A>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">{tr("opay.ctaTitle")}</h2>
            <p className="text-base text-slate-300 mt-4 max-w-md mx-auto">{tr("opay.ctaDesc")}</p>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-8 font-semibold bg-[#00C853] text-white rounded-full px-8 py-3.5 text-sm hover:bg-[#00A844] transition-all">{tr("hero.cta1")} <ArrowRight size={16} /></Link>
          </A>
        </div>
      </section>
    </div>
  );
}
